import { Logger, AgentSessionManager } from '@nice-ccf/core-sdk';
import { Subject } from 'rxjs/internal/Subject';
import { AgentLegService } from './service/agent-leg-service';
import { CXoneAgentQueuesProvider } from './provider/cxone-agent-queues-provider';
import { CXoneAgentQueuesDetailProvider } from './provider/cxone-agent-queues-detail-provider';
import { AgentQueuesDetail, AgentQueues } from '@nice-ccf/common-sdk';
import { CXoneSkillActivityProvider } from './provider/cxone-skill-activity-provider';
import { SkillActivityEvent } from './model/skill-activity-event';
import { IndicatorManager } from './contact/indicator-manager';
import { ContactManager } from './contact/contact-manager';
import { CommitmentService } from '../commitment/model/commitment-service';
import { WemSchedule } from '../wem-schedule/wem-schedule';


/** This is the base class for ACD */
export class CXoneClient {
  private logger: Logger = new Logger('ACD', 'CXoneClient');
  private static singleton: CXoneClient;
  private agentSession = <AgentSessionManager>{};
  private agentQueueProvider = new CXoneAgentQueuesProvider();
  private agentQueuesDetailProvider = new CXoneAgentQueuesDetailProvider();
  private skillActivityProvider: CXoneSkillActivityProvider = new CXoneSkillActivityProvider();
  public indicatorManager = new IndicatorManager();
  public agentLegService = <AgentLegService>{};
  public agentQueueSubject: Subject<AgentQueues> = new Subject();
  public agentQueuesDetailSubject: Subject<AgentQueuesDetail[]> = new Subject();
  public skillActivityEvent = new Subject<SkillActivityEvent[]>();
  public commitment = <CommitmentService>{};
  public wemSchedule = <WemSchedule>{}; 
  public contactManager = <ContactManager>{};

  /**
   * get instance for agent session and admin service
   * @example
   * ```
   * const cxoneClient = new CXoneClient();
   * ```
   */
  constructor() {
    this.agentSession = AgentSessionManager.instance;
    this.commitment = new CommitmentService();
    this.wemSchedule = new WemSchedule();
    this.initialize();
  }

  /**
   * Method to create singleton object of the class
   * ```
   * @example
   * const cxoneClient = CXoneClient.instance();
   * ```
   */
  public static get instance(): CXoneClient {
    if (!CXoneClient.singleton) {
      CXoneClient.singleton = new CXoneClient();
    }
    return CXoneClient.singleton;
  }

  /**
   * Method to create instance for class
   */
  private initialize() {
    this.contactManager = new ContactManager();
    this.agentLegService = new AgentLegService();
    this.indicatorManager = new IndicatorManager();
    this.agentQueueProvider.setACDSdkBaseInstance(this);
    this.agentQueuesDetailProvider.setACDSdkBaseInstance(this);
    this.skillActivityProvider.setACDSdkBaseInstance(this);
  }

  /**
   * Used to start the skill activity polling
   * @example -
   * ```
   * this.CXoneClient.startSkillActivityPolling()
   * ```
   */
  public startSkillActivityPolling() {
    this.skillActivityProvider.startPolling();
  }

  /**
   * Used to terminate the skill activity polling
   * @example -
   * ```
   * this..terminateSkillActivityPolling()
   * ```
   */
  public terminateSkillActivityPolling() {
    this.skillActivityProvider.terminatePolling();
  }

  /**
   * @example -
   * ```
   * const agentLegEvent = CXoneClient.instance.agentLegEvent
   * ```
   */
  get agentLegEvent() {
    return this.agentSession.agentLegSubject;
  }

  /**
   * Used to start the agent queue polling
   * @example -
   * ```
   * this.cxoneClient.startAgentQueuesPolling()
   * ```
   */
  public startAgentQueuesPolling(agentId: string) {
    this.agentQueueProvider.agentQueuesPolling(agentId);
    this.agentQueuesDetailProvider.agentQueuesDetailsPolling(agentId);
  }

  /**
   * Used to terminate the agent queue polling
   * @example -
   * ```
   * this.cxoneClient.terminateAgentQueuePolling()
   * ```
   */
  public terminateAgentQueuePolling() {
    this.agentQueueProvider.terminatePolling();
    this.agentQueuesDetailProvider.terminatePolling();
  }

  /**
   * @example
   * ```
   * const contactIndicator = acdsdk.contactIndicatorsEvent
   * ```
   */
  get contactIndicatorsEvent() {
    return this.indicatorManager.contactIndicatorSubject;
  }

  /**
   * @example
   * ```
   * const agentIndicator = acdsdk.agentIndicatorsEvent
   * ```
   */
  get agentIndicatorsEvent() {
    return this.indicatorManager.agentIndicatorSubject;
  }
}
